import { useState, useEffect, type ReactNode } from 'react';
import {
  doc, collection,   getDocs, runTransaction,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { logActivity } from '../utils/activity';
import { useSettings } from '../hooks/useSettings';
import { Button, Modal, ModalBanner, FormField } from './ui';
import { fmtMoney, getCurrencySymbol, findExchangeRate } from '../utils/currency';
import { buildEntry, postEntry, entryId, linesBrokerPayment } from '../utils/ledger';
import { isIndian, invoiceGst, gstConflict, brokerGstOf, gstPercentsOf, type GstInput } from '../utils/tradingSystem';
import { PaidGstFields } from './GstBoxes';
import type { PaymentStatus, BankAccount } from '../types';

function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}

function todayISO() {
  return new Date().toISOString().split('T')[0];
}
// Invoice No is stored in full, but shown here as just the prefix + last 4
// digits to keep the header compact. Keeping the prefix matters — a bare
// last-4 truncation would collapse different prefixes into the same
// number, making them indistinguishable.
function lotNoDisplay(invoiceNo?: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}

interface PaymentForm {
  bankAccountId: string;
  amount:        string;
  rate:          string;
  paidDate:      string;
  referenceNo:   string;
  notes:         string;
}

const EMPTY: PaymentForm = {
  bankAccountId: '',
  amount: '', rate: '', paidDate: todayISO(),
  referenceNo: '', notes: '',
};

// Deliberately a minimal structural shape, not SaleInvoice | PurchaseInvoice
// — every existing call site already passes one of those (both satisfy this
// automatically), and Brokers.tsx's own LineDeal (a per-line Broker1/Broker2
// deal, not a real invoice doc) now passes this too, since it carries the
// exact same field names for this reason.
interface BrokerPayableDeal {
  id: string;
  invoiceNo: string;
  brokerCommissionUSD?: number;
  brokerPaidUSD?: number;
  brokerEntityId?: string;
}

interface Props {
  invoice: BrokerPayableDeal | null;
  onClose: () => void;
  /** Which collection the invoice lives in — drives where the brokerPayments doc + invoice update are written. */
  source?: 'sale' | 'purchase';
  /** Resolved broker name for the header title / transaction description — callers already have an entity lookup handy. */
  brokerName?: string;
  // Set only when this payment is against one of the per-line Broker1/
  // Broker2 brokers rather than the invoice's own single header-level
  // broker — tags the payment doc with this id and, critically, does NOT
  // update the invoice's own brokerPaidUSD/brokerPaymentStatus (those are
  // the header broker's alone; a line-broker's paid/outstanding is instead
  // derived live by summing this subcollection filtered to its own
  // brokerEntityId — see Brokers.tsx's lineBrokerPaid).
  lineBrokerEntityId?: string;
}

// The one identifier worth calling out from the rest — same highlighted pill
// used for Invoice No everywhere else in the app.
function LotPill({ children }: { children: ReactNode }) {
  return (
    <span style={{
      display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
      fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 5, letterSpacing: '0.02em',
    }}>
      {children}
    </span>
  );
}

// ── Mini icons ────────────────────────────────────────────────────────────────

function IconCash() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <rect x="1.5" y="5" width="17" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 10h1M14 10h1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconBank() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <rect x="2" y="8" width="16" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2 8l8-5 8 5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M5 11.5v3.5M10 11.5v3.5M15 11.5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
function IconCheck() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7.5l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
// Mirrors RecordPaymentModal.tsx exactly, but for broker commission paid OUT
// against a SaleInvoice's brokerCommissionUSD (rather than the customer's own
// totalAmountUSD) — written under sales/{id}/brokerPayments instead of
// invoices/{id}/payments or sales/{id}/payments.

export default function RecordBrokerPaymentModal({ invoice, onClose, source = 'sale', brokerName, lineBrokerEntityId }: Props) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const baseCurrency = settings.currency;
  const coll = source === 'purchase' ? 'invoices' : 'sales';
  const [form,     setForm]     = useState<PaymentForm>(EMPTY);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [rateTouched, setRateTouched] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  // Indian trading system: GST on this brokerage.
  const indianGst = isIndian(settings);
  const [gstIn, setGstIn] = useState<GstInput>({ cgst: '', sgst: '', igst: '' });
  const [gstNotClaimable, setGstNotClaimable] = useState(false);
  const [gstReverse, setGstReverse] = useState(false);

  useEffect(() => {
    if (!invoice || !business) return;
    const db = getClientDb();
    if (!db) return;
    getDocs(
      collection(db, 'bankAccounts'),
    ).then(snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() } as BankAccount));
      const accs = all
        .filter(a => a.isActive !== false)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      setAccounts(accs);
      const def = accs.find(a => a.isDefault) ?? accs[0];
      if (def) setForm(f => ({ ...f, bankAccountId: def.id }));
    }).catch(err => console.error('Failed to load bank accounts:', err));
  }, [invoice, business]);

  // The commission itself always accrues in the business's base currency
  // (brokerCommissionUSD) — conversion only kicks in when the paying
  // account uses a different one. Computed (and their two effects) before
  // the early return below, since they don't depend on `invoice` — same
  // Rules-of-Hooks reasoning as RecordPaymentModal.tsx.
  const selectedAccount = accounts.find(a => a.id === form.bankAccountId) ?? null;
  const needsConversion = !!selectedAccount && selectedAccount.currency !== baseCurrency;
  const configuredRate = needsConversion ? findExchangeRate(settings.exchangeRates, baseCurrency, selectedAccount!.currency) : null;

  useEffect(() => { setRateTouched(false); }, [form.bankAccountId]);
  useEffect(() => {
    if (!needsConversion || rateTouched || !configuredRate) return;
    setForm(f => ({ ...f, rate: String(configuredRate) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsConversion, rateTouched, configuredRate]);

  if (!invoice) return null;
  const inv = invoice; // narrowed, non-null copy — TS doesn't carry the `if` narrowing into nested closures like handleSave below

  // The commission-side figures (Amount typed, Commission Summary, overpay
  // check) are always in the base currency — never the paying account's
  // own currency, which may differ (see needsConversion above).
  const currency        = baseCurrency;
  const sym             = getCurrencySymbol(currency);

  const commissionTotal = invoice.brokerCommissionUSD ?? 0;
  const alreadyPaid  = invoice.brokerPaidUSD ?? 0;
  const outstanding  = Math.max(0, commissionTotal - alreadyPaid);
  const payAmt       = parseFloat(form.amount) || 0;
  const remaining    = Math.max(0, outstanding - payAmt);
  // Indian trading system: GST on the brokerage. The commission settled is
  // payAmt; the bank also pays the GST (not on reverse charge).
  const gstPcts      = indianGst ? gstPercentsOf(gstIn) : {};
  const gstTotal     = indianGst && payAmt > 0 ? invoiceGst(payAmt, gstPcts).total : 0;
  const paidWithGst  = gstTotal > 0 && !gstReverse ? Math.round((payAmt + gstTotal) * 100) / 100 : payAmt;
  // The figure that actually moves in the paying account's own currency —
  // payAmt itself when no conversion is needed, otherwise payAmt × the
  // (auto-filled-but-editable) Conversion Rate.
  const rateNum      = needsConversion ? (parseFloat(form.rate) || 0) : 1;
  const bankAmt      = needsConversion ? paidWithGst * rateNum : paidWithGst;
  const afterBalance = selectedAccount ? selectedAccount.currentBalance - bankAmt : null;
  const isOverpaying = payAmt > outstanding + 0.005;

  const previewStatus: PaymentStatus =
    payAmt >= outstanding - 0.005 && payAmt > 0 ? 'paid'
    : payAmt > 0 ? 'partial'
    : 'unpaid';

  function set<K extends keyof PaymentForm>(k: K, v: PaymentForm[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  function validate(): string {
    if (!form.bankAccountId) return 'Select an account to pay from.';
    const amt = parseFloat(form.amount);
    if (!form.amount || isNaN(amt) || amt <= 0) return 'Enter a valid amount.';
    if (amt > outstanding + 0.005) return `Payment amount exceeds the outstanding commission of ${fmtMoney(outstanding, currency)}. Please adjust the amount before proceeding.`;
    if (needsConversion) {
      const r = parseFloat(form.rate);
      if (!form.rate || isNaN(r) || r <= 0) {
        return `Enter the conversion rate to ${selectedAccount!.currency} — 1 ${baseCurrency} equals how many ${selectedAccount!.currency}.`;
      }
    }
    if (!form.paidDate) return 'Select payment date.';
    if (indianGst && gstConflict(gstPcts)) return 'Use CGST + SGST, or IGST, not both.';
    return '';
  }

  async function handleSave() {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }

    const amt        = parseFloat(form.amount);
    const bankAmount = needsConversion ? paidWithGst * parseFloat(form.rate) : paidWithGst;
    const gstFields  = gstTotal > 0
      ? { gst: { cgstPct: gstPcts.cgstPct ?? null, sgstPct: gstPcts.sgstPct ?? null, igstPct: gstPcts.igstPct ?? null },
          gstTotalUSD: gstTotal, gstNotClaimable: gstNotClaimable || undefined, gstReverseCharge: gstReverse || undefined }
      : {};
    const newPaid = alreadyPaid + amt;
    const status: PaymentStatus =
      newPaid >= commissionTotal - 0.005 ? 'paid'
      : newPaid > 0 ? 'partial'
      : 'unpaid';

    const account = selectedAccount!;

    try {
      const nowISO = new Date().toISOString();
      const payRef = doc(collection(db, coll, inv.id, 'brokerPayments'));
      const invRef = doc(db, coll, inv.id);
      const accRef = doc(db, 'bankAccounts', account.id);
      const txRef  = doc(collection(db, 'bankTransactions'));

      await runTransaction(db, async tx => {
        const accSnap = await tx.get(accRef);
        if (!accSnap.exists()) throw new Error('Bank account no longer exists.');

        logActivity(tx, db, business!.id, user, {
          module: 'broker', action: 'create', recordId: inv.id, recordNo: inv.invoiceNo,
          partyId: lineBrokerEntityId || inv.brokerEntityId, amountUSD: amt,
          note: [account.name, form.referenceNo.trim() ? `Ref ${form.referenceNo.trim()}` : ''].filter(Boolean).join(' · '),
        });
        tx.set(payRef, stripUndef({
          saleId:             source === 'sale'     ? inv.id : undefined,
          purchaseId:         source === 'purchase' ? inv.id : undefined,
          brokerEntityId:     lineBrokerEntityId || undefined,
          businessId:         business!.id,
          amount:             amt,
          currency:           baseCurrency,
          // With GST the bank moves more than the commission: keep that figure.
          bankAmount:         needsConversion || gstTotal > 0 ? bankAmount : undefined,
          bankCurrency:       needsConversion ? account.currency : undefined,
          exchangeRate:       needsConversion ? bankAmount / paidWithGst : undefined,
          ...gstFields,
          paidDate:           form.paidDate,
          bankAccountId:      account.id,
          bankAccountName:    account.name,
          bankTransactionId:  txRef.id,   // link back so edits/deletes can find it
          referenceNo:        form.referenceNo.trim() || undefined,
          notes:              form.notes.trim()        || undefined,
          createdAt:          nowISO,
          createdBy:       user!.id,
        }));

        // A per-line broker's paid/outstanding is derived live (see
        // BrokerPayableDeal above) — the invoice's own brokerPaidUSD/
        // brokerPaymentStatus belong to the header broker alone and must
        // stay untouched by a line-broker payment.
        if (!lineBrokerEntityId) {
          tx.update(invRef, {
            brokerPaidUSD:       newPaid,
            brokerPaymentStatus: status,
            updatedAt:           nowISO,
          });
        }

        tx.set(txRef, stripUndef({
          businessId:    business!.id,
          accountId:     account.id,
          accountName:   account.name,
          currency:      account.currency,
          direction:     'out',
          amount:        bankAmount,
          date:          form.paidDate,
          description:   `${brokerName || 'Broker commission'} — (${lotNoDisplay(inv.invoiceNo)})`,
          referenceType: 'broker_payment',
          referenceId:   inv.id,
          referenceNo:   form.referenceNo.trim() || undefined,
          notes:         form.notes.trim()        || undefined,
          createdAt:     nowISO,
          createdBy:     user!.id,
        }));

        const currentBal = (accSnap.data() as BankAccount).currentBalance;
        tx.update(accRef, { currentBalance: currentBal - bankAmount });

        // Ledger (additive). Dr Broker Commission Payable / Cr the bank
        // account — stays in the base currency, same as the commission
        // itself; only the bank side above actually converts. The broker
        // this settles is the per-line one when paying against a LineDeal,
        // otherwise the invoice's own header-level broker.
        const payingBrokerEntityId = lineBrokerEntityId ?? inv.brokerEntityId;
        if (payingBrokerEntityId) {
          postEntry(tx, db, buildEntry({
            businessId: business!.id,
            sourceType: 'broker_payment',
            sourceId: payRef.id,
            sourceCollection: `${coll}/${inv.id}/brokerPayments`,
            date: form.paidDate,
            currency: baseCurrency,
            createdBy: user!.id,
            note: `Broker payout — ${brokerName || 'broker'} (${lotNoDisplay(inv.invoiceNo)})`,
            lines: linesBrokerPayment(amt, payingBrokerEntityId, account.id, brokerGstOf(gstFields)),
          }), entryId('broker_payment', payRef.id));
        }
      });

      setForm(EMPTY);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to record payment.');
    }
    setSaving(false);
  }

  function handleClose() { setForm(EMPTY); setError(''); onClose(); }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Modal
      open
      onClose={handleClose}
      hideHead
      size="lg"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={handleClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving} disabled={isOverpaying}>Record Payment</Button>
          </div>
        </div>
      }
    >
      <ModalBanner
        title={brokerName || 'Record Payment'}
        eyebrow="Broker Commission Payment"
        right={
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>
              Invoice No
            </div>
            <LotPill>{lotNoDisplay(invoice.invoiceNo)}</LotPill>
          </div>
        }
        onClose={handleClose}
      />

      {/* ── Two-column desktop layout ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 24, alignItems: 'start' }}>

        {/* ── LEFT: form ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Pay From */}
          <div>
            <div className="form-section-label" style={{ marginBottom: 10 }}>Pay From</div>
            {accounts.length === 0 ? (
              <div className="msg msg-info" style={{ margin: 0, fontSize: 12 }}>
                No bank accounts found. Add one in Bank → Accounts first.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {accounts.map(acc => {
                  const isSelected = form.bankAccountId === acc.id;
                  return (
                    <button
                      key={acc.id}
                      type="button"
                      onClick={() => set('bankAccountId', acc.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '11px 14px', borderRadius: 'var(--r-md)',
                        border: isSelected ? '2px solid var(--primary)' : '1.5px solid var(--border)',
                        background: isSelected ? 'var(--primary-light)' : 'var(--surface)',
                        cursor: 'pointer', textAlign: 'left', width: '100%',
                        transition: 'border-color 0.14s, background 0.14s',
                      }}
                    >
                      {/* Account icon */}
                      <div style={{
                        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: acc.type === 'cash'
                          ? (isSelected ? 'color-mix(in srgb, var(--success) 22%, var(--success-bg))' : 'var(--success-bg)')
                          : (isSelected ? 'color-mix(in srgb, var(--primary) 22%, var(--primary-light))' : 'var(--primary-light)'),
                        color: acc.type === 'cash' ? 'var(--success)' : 'var(--primary)',
                      }}>
                        {acc.type === 'cash' ? <IconCash /> : <IconBank />}
                      </div>

                      {/* Name + detail */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-1)', marginBottom: 2 }}>
                          {acc.name}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                          {acc.type === 'bank' && acc.bankName ? acc.bankName : 'Cash'}
                          {acc.accountNumber ? ` · ****${acc.accountNumber.slice(-4)}` : ''}
                          {' · '}{acc.currency}
                        </div>
                      </div>

                      {/* Balance + check */}
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontWeight: 700, fontFamily: 'var(--font-num)', fontSize: 13, color: 'var(--text-1)' }}>
                          {fmtMoney(acc.currentBalance, acc.currency)}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-3)' }}>available</div>
                      </div>

                      {/* Check indicator */}
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: isSelected ? 'var(--primary)' : 'var(--surface-2)',
                        border: isSelected ? 'none' : '1.5px solid var(--border)',
                        color: '#fff',
                        transition: 'background 0.14s',
                      }}>
                        {isSelected && <IconCheck />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Payment details */}
          <div>
            <div className="form-section-label" style={{ marginBottom: 10 }}>Payment Details</div>

            <div className="form-row-2">
              <FormField label={indianGst ? `Commission (${currency})` : `Amount (${currency})`} required>
                <div style={{ position: 'relative' }}>
                  <div className="input-wrap has-prefix" style={isOverpaying ? { borderColor: 'var(--danger)' } : undefined}>
                    <span className="input-prefix">{sym}</span>
                    <input
                      type="number" step="0.01" min="0.01"
                      value={form.amount}
                      onChange={e => set('amount', e.target.value)}
                      placeholder={outstanding.toFixed(2)}
                      autoFocus
                    />
                  </div>
                  {outstanding > 0 && (
                    <button
                      type="button"
                      onClick={() => set('amount', outstanding.toFixed(2))}
                      style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                        background: 'var(--primary-light)', color: 'var(--primary)',
                        border: 'none', borderRadius: 4, fontSize: 10, fontWeight: 700,
                        padding: '2px 7px', cursor: 'pointer', letterSpacing: '0.02em',
                      }}
                    >
                      FULL
                    </button>
                  )}
                </div>
                {isOverpaying && (
                  <p className="form-hint" style={{ color: 'var(--danger)', fontWeight: 600, marginTop: 4 }}>
                    Payment amount exceeds the outstanding commission of {fmtMoney(outstanding, currency)}. Please adjust the amount before proceeding.
                  </p>
                )}
              </FormField>
              <FormField label="Payment Date" required>
                <input type="date" value={form.paidDate} onChange={e => set('paidDate', e.target.value)} />
              </FormField>
            </div>

            {indianGst && (
              <PaidGstFields base={payAmt} value={gstIn} onChange={setGstIn}
                notClaimable={gstNotClaimable} onNotClaimable={setGstNotClaimable}
                reverseCharge={gstReverse} onReverseCharge={setGstReverse}
                money={v => fmtMoney(v, currency)} />
            )}

            {needsConversion && (
              <div className="form-row-2">
                <FormField
                  label={`Conversion Rate (1 ${baseCurrency} = ? ${selectedAccount!.currency})`}
                  required
                  hint={configuredRate
                    ? `Pre-filled from Settings › Dashboard's Exchange Rates card — adjust if the bank's actual rate today differs.`
                    : `No exchange rate configured for ${baseCurrency} → ${selectedAccount!.currency} — enter today's rate.`}
                >
                  <input
                    className="no-spinner"
                    type="number" step="0.0001" min="0.0001"
                    value={form.rate}
                    onChange={e => { set('rate', e.target.value); setRateTouched(true); }}
                    placeholder="0.0000"
                  />
                </FormField>
                <FormField label={`Converted Amount (${selectedAccount!.currency})`}>
                  <input value={bankAmt > 0 ? bankAmt.toFixed(2) : ''} placeholder="0.00" disabled />
                </FormField>
              </div>
            )}

            <FormField label="Reference No." hint="Bank ref, wire ref, cheque number, etc.">
              <input
                value={form.referenceNo}
                onChange={e => set('referenceNo', e.target.value)}
                placeholder="Optional"
                style={{ fontFamily: 'var(--font-num)' }}
              />
            </FormField>

            <FormField label="Notes (optional)">
              <textarea
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
                placeholder="Any notes about this payment…"
                style={{ minHeight: 60, resize: 'vertical' }}
              />
            </FormField>
          </div>
        </div>

        {/* ── RIGHT: summary panel ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Commission breakdown */}
          <div style={{
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)', overflow: 'hidden',
          }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-2)', fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Commission Summary
            </div>
            {[
              { label: 'Commission Owed', val: commissionTotal, color: 'var(--text-1)' },
              { label: 'Already Paid',    val: alreadyPaid,     color: 'var(--success)' },
              { label: 'Outstanding',     val: outstanding,     color: outstanding > 0 ? 'var(--danger)' : 'var(--text-3)' },
            ].map(({ label, val, color }, i, arr) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px',
                borderBottom: i < arr.length - 1 ? '1px solid var(--border-2)' : 'none',
              }}>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{label}</span>
                <span style={{ fontFamily: 'var(--font-num)', fontSize: 13, fontWeight: 700, color }}>
                  {sym + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            ))}
          </div>

          {/* After-payment preview — only shown when amount is entered */}
          {payAmt > 0 && (
            <div style={{
              background: previewStatus === 'paid' ? 'var(--success-bg)' : 'var(--surface-2)',
              border: `1px solid ${previewStatus === 'paid' ? 'color-mix(in srgb, var(--success) 35%, transparent)' : 'var(--border)'}`,
              borderRadius: 'var(--r-md)', overflow: 'hidden',
            }}>
              <div style={{ padding: '10px 14px', borderBottom: `1px solid ${previewStatus === 'paid' ? 'color-mix(in srgb, var(--success) 35%, transparent)' : 'var(--border-2)'}`, fontSize: 11, fontWeight: 700, color: previewStatus === 'paid' ? 'var(--success)' : 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                After Payment
              </div>

              <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>You pay</span>
                  <span style={{ fontFamily: 'var(--font-num)', fontSize: 14, fontWeight: 800, color: 'var(--danger)' }}>
                    −{sym}{payAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Still owed</span>
                  <span style={{ fontFamily: 'var(--font-num)', fontSize: 13, fontWeight: 700, color: remaining > 0 ? 'var(--danger)' : 'var(--success)' }}>
                    {sym + remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>Commission status</span>
                  <span className={`badge badge-${previewStatus === 'paid' ? 'active' : previewStatus === 'partial' ? 'warning' : 'draft'}`} style={{ fontSize: 11 }}>
                    {previewStatus === 'paid' ? '✓ Paid' : previewStatus === 'partial' ? 'Partial' : 'Unpaid'}
                  </span>
                </div>
                {afterBalance !== null && (
                  <div style={{
                    marginTop: 4, paddingTop: 8, borderTop: `1px solid ${previewStatus === 'paid' ? 'color-mix(in srgb, var(--success) 35%, transparent)' : 'var(--border-2)'}`,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{selectedAccount?.name} balance</span>
                    <span style={{ fontFamily: 'var(--font-num)', fontSize: 12, fontWeight: 700, color: afterBalance < 0 ? 'var(--danger)' : 'var(--text-2)' }}>
                      {fmtMoney(afterBalance, selectedAccount?.currency ?? currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Placeholder when no amount typed */}
          {payAmt === 0 && (
            <div style={{
              border: '1.5px dashed var(--border)', borderRadius: 'var(--r-md)',
              padding: '18px 14px', textAlign: 'center',
              color: 'var(--text-3)', fontSize: 12,
            }}>
              Enter an amount to see payment preview
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
